import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import {
  ConfirmationService,
  InPostPointSelect,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { PublicSubscriptionCollectionItem } from '../models/public-subscription-collection-item';
import { PublicSubscriptionApiService } from '../api/services/public-subscription-api.service';
import { CollectionRequestBuilder } from '@web/common';
import { PublicSubscriptionCollectionFilter } from '../models/public-subscription-collection-filter';
import { SubscriptionNumber } from '../../shared/types';
import { executeOrNull, once } from '@web/utils';
import {
  dictionaryCountryFromResponse,
  phoneNumberWithPrefixFromResponse,
  phoneNumberWithPrefixToRequest,
} from '@web/shared/module/dictionary';
import { PublicSubscriptionDetails } from '../models/public-subscription-details';
import { PublicSubscriptionBillingAddress } from '../models/public-subscription-billing-address';
import { PublicPaymentMethod, PublicSubscriptionForm } from '../models';
import { PublicSubscriptionShippingAddress } from '../models/public-subscription-shipping-address';
import {
  refundSummaryFromResponse,
  shippingMethodDictionaryFromResponse,
} from '../../shared/utils';
import { PublicSubscriptionItem } from '../models/public-subscription-item';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { PublicSubscriptionOrderCollectionItem } from '../models/public-subscription-order-collection-item';
import { PublicSubscriptionOrderCollectionFilter } from '../models/public-subscription-order-collection-filter';
import { PublicSubscriptionBillingAddressForm } from '../models/public-subscription-billing-address-form';
import { PublicSubscriptionBillingAddressUpdateRequest } from '../api/requests/public-subscription-billing-address-update.request';
import { PublicSubscriptionShippingAddressForm } from '../models/public-subscription-shipping-address-form';
import { PublicSubscriptionShippingAddressUpdateRequest } from '../api/requests/public-subscription-shipping-address-update.request';
import { PublicSubscriptionUpdateRequest } from '../api/requests/public-subscription-update.request';

@Injectable({
  providedIn: 'root',
})
export class PublicSubscriptionDataSourceService {
  private readonly subscriptionApi = inject(PublicSubscriptionApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly toast = inject(ToastService);
  private readonly confirmation = inject(ConfirmationService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<PublicSubscriptionCollectionFilter>,
  ): Observable<TableDataPayload<PublicSubscriptionCollectionItem>> {
    /* prettier-ignore */
    return this.subscriptionApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('number').build())
                .appendText((builder) => builder.equalFor('totalPrice').build())
                .appendText((builder) => builder.equalFor('status').build())
                .appendDate((builder) => builder.rangeFor('nextPaymentDate').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new PublicSubscriptionCollectionItem({
                    canBillingAddressBeUpdated: item.contextOperations.canBillingAddressBeUpdated,
                    canShippingAddressBeUpdated: item.contextOperations.canShippingAddressBeUpdated,
                    canBeViewed: item.contextOperations.canBeViewed,
                    canBeCancelled: item.contextOperations.canBeCancelled,
                    canBeResumed: item.contextOperations.canBeResumed,
                    canBeSuspended: item.contextOperations.canBeSuspended,
                    canBeUpdated: item.contextOperations.canBeUpdated,
                    totalPrice: item.totalPrice,
                    id: item.id,
                    isLocked: item.isLocked,
                    number: item.number,
                    status: item.status,
                    nextPaymentDate: item.nextPaymentDate,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  /* prettier-ignore */
  public fetchSubscriptionOrderCollection(options: {
    number: SubscriptionNumber;
    lazyLoad: TableLazyLoad<PublicSubscriptionOrderCollectionFilter>;
  }): Observable<TableDataPayload<PublicSubscriptionOrderCollectionItem>> {
    const { lazyLoad } = options;
    return this.subscriptionApi
      .fetchSubscriptionOrderCollection({
        number: options.number,
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('number').build())
              .appendText((builder) => builder.equalFor('totalItems').build())
              .appendText((builder) => builder.equalFor('status').build())
              .appendText((builder) => builder.equalFor('totalPrice').build())
              .buildAsArray(),
          )
          .build(),
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new PublicSubscriptionOrderCollectionItem({
                  number: item.number,
                  status: item.status,
                  totalItems: item.totalItems,
                  id: item.id,
                  totalPrice: item.totalPrice,
                  refundSummary: executeOrNull(item.refundSummary, (summary) => refundSummaryFromResponse(summary)),
                  createdAt: item.createdAt,
                }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public confirmAndCancelByNumber(
    number: SubscriptionNumber,
  ): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz anulować wybraną subskrypcję?')
          .accept(() => {
            this.subscriptionApi
              .cancelByNumber(number)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrana subskrypcja została anulowana.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }
  public confirmAndSuspendByNumber(
    number: SubscriptionNumber,
  ): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz wstrzymać wybraną subskrypcję?')
          .accept(() => {
            this.subscriptionApi
              .suspendByNumber(number)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrana subskrypcja została wstrzymana.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndResumeByNumber(
    number: SubscriptionNumber,
  ): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz wznowić wybraną subskrypcję?')
          .accept(() => {
            this.subscriptionApi
              .resumeByNumber(number)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrana subskrypcja została wznowiona.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public fetchFormByNumber(
    number: SubscriptionNumber,
  ): Observable<PublicSubscriptionForm> {
    return this.subscriptionApi.fetchDetailsByNumber({ number }).pipe(
      map(
        (response) =>
          new PublicSubscriptionForm({
            cardChargeDay: response.cardChargeDay,
          }),
      ),
    );
  }

  public updateSubscriptionByForm(params: {
    number: SubscriptionNumber;
    data: PublicSubscriptionForm;
  }): Observable<void> {
    const { number, data } = params;
    const request = new PublicSubscriptionUpdateRequest({
      cardChargeDay: data.cardChargeDay,
    });

    return this.subscriptionApi.updateSubscription({ number, request }).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder.initSuccess('Subskrypcja została zaktualizowana.').build(),
        );
      }),
    );
  }

  public fetchDetailsByNumber(
    number: SubscriptionNumber,
  ): Observable<PublicSubscriptionDetails> {
    return this.subscriptionApi.fetchDetailsByNumber({ number }).pipe(
      map(
        (response) =>
          /* prettier-ignore */
          new PublicSubscriptionDetails({
            canShippingAddressBeUpdated: response.contextOperations.canShippingAddressBeUpdated,
            canBillingAddressBeUpdated: response.contextOperations.canBillingAddressBeUpdated,
            cardChargeDay: response.cardChargeDay,
            id: response.id,
            isLocked: response.isLocked,
            number: response.number,
            status: response.status,
            totalPrice: response.totalPrice,
            itemsPrice: response.itemsPrice,
            shippingPrice: response.shippingPrice,
            lastPaymentDate: response.lastPaymentDate,
            nextPaymentDate: response.nextPaymentDate,
            cancelledDate: response.cancelledDate,
            paymentMethod: new PublicPaymentMethod({
              name: response.paymentMethod.name,
              id: response.paymentMethod.name,
              code: response.paymentMethod.code,
            }),
            shippingMethod: shippingMethodDictionaryFromResponse(response.shippingMethod),
            shippingAddress: new PublicSubscriptionShippingAddress({
              city: response.shippingAddress.city,
              dignity: response.shippingAddress.dignity,
              email: response.shippingAddress.email,
              firstName: response.shippingAddress.firstName,
              lastName: response.shippingAddress.lastName,
              phone: phoneNumberWithPrefixFromResponse(response.shippingAddress.phone),
              postCode: response.shippingAddress.postCode,
              streetLine1: response.shippingAddress.streetLine1,
              country: dictionaryCountryFromResponse(
                response.shippingAddress.country,
              ),
              streetLine2: response.shippingAddress.streetLine2,
              state: response.shippingAddress.state,
              companyName: response.shippingAddress.companyName,
              shippingPointName: response.shippingAddress.shippingPointName,
              shippingPoint: response.shippingAddress.shippingPoint,
            }),
            billingAddress: new PublicSubscriptionBillingAddress({
              city: response.billingAddress.city,
              dignity: response.billingAddress.dignity,
              email: response.billingAddress.email,
              firstName: response.billingAddress.firstName,
              lastName: response.billingAddress.lastName,
              phone: phoneNumberWithPrefixFromResponse(response.billingAddress.phone),
              postCode: response.billingAddress.postCode,
              streetLine1: response.billingAddress.streetLine1,
              country: dictionaryCountryFromResponse(
                response.billingAddress.country,
              ),
              streetLine2: response.billingAddress.streetLine2,
              state: response.billingAddress.state,
              companyName: response.billingAddress.companyName,
              companyNip: response.billingAddress.companyNip,
            }),
            items: response.items.map(
              (item) =>
                new PublicSubscriptionItem({
                  name: item.name,
                  price: item.price,
                  slug: item.slug,
                  featuredFile: executeOrNull(item.featuredFile, (file) =>
                    multimediaFromResponse(file),
                  ),
                  type: item.type,
                  quantity: item.quantity,
                  unitPrice: item.unitPrice,
                }),
            ),
          }),
      ),
    );
  }

  public fetchBillingAddressFormByNumber(
    number: SubscriptionNumber,
  ): Observable<PublicSubscriptionBillingAddressForm> {
    return this.subscriptionApi.fetchBillingAddressDetails(number).pipe(
      map(
        (response) =>
          new PublicSubscriptionBillingAddressForm({
            city: response.city,
            companyName: response.companyName,
            companyNip: response.companyNip,
            countryId: response.country?.id,
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            phone: phoneNumberWithPrefixFromResponse(response.phone),
            postCode: response.postCode,
            state: response.state,
            streetLine1: response.streetLine1,
            streetLine2: response.streetLine2,
          }),
      ),
    );
  }

  public fetchShippingAddressFormByNumber(
    number: SubscriptionNumber,
  ): Observable<PublicSubscriptionShippingAddressForm> {
    return this.subscriptionApi.fetchShippingAddressDetails(number).pipe(
      map(
        (response) =>
          new PublicSubscriptionShippingAddressForm({
            city: response.city,
            companyName: response.companyName,
            countryId: response.country?.id,
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            phone: phoneNumberWithPrefixFromResponse(response.phone),
            postCode: response.postCode,
            state: response.state,
            streetLine1: response.streetLine1,
            streetLine2: response.streetLine2,
            shippingMethodId: response.shippingMethod?.id,
            shippingPoint: executeOrNull(response.shippingPoint, (result) => {
              return new InPostPointSelect({
                description: result,
              });
            }),
            properties: {
              shippingPrice: response.shippingPrice,
              totalPrice: response.totalPrice,
              shippingMethod: shippingMethodDictionaryFromResponse(
                response.shippingMethod,
              ),
              shippingPoint: response.shippingPoint,
            },
          }),
      ),
    );
  }

  public updateBillingAddressByFormData(param: {
    number: string;
    data: PublicSubscriptionBillingAddressForm;
  }): Observable<void> {
    const { number, data } = param;
    const request = new PublicSubscriptionBillingAddressUpdateRequest({
      city: data.city!,
      companyName: data.companyName,
      companyNip: data.companyNip,
      countryId: data.countryId!,
      email: data.email!,
      firstName: data.firstName!,
      lastName: data.lastName!,
      phone: phoneNumberWithPrefixToRequest(data.phone!),
      postCode: data.postCode!,
      state: data.state,
      streetLine1: data.streetLine1!,
      streetLine2: data.streetLine2,
    });

    return this.subscriptionApi.updateBillingAddress({ number, request }).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder
            .initSuccess(
              'Adres rozliczeniowy dla subskrypcji został zaktualizowany.',
            )
            .build(),
        );
      }),
    );
  }

  public updateShippingAddressByFormData(param: {
    number: SubscriptionNumber;
    data: PublicSubscriptionShippingAddressForm;
  }): Observable<void> {
    const { number, data } = param;
    const request = new PublicSubscriptionShippingAddressUpdateRequest({
      city: data.city!,
      companyName: data.companyName,
      countryId: data.countryId!,
      email: data.email!,
      firstName: data.firstName!,
      lastName: data.lastName!,
      phone: phoneNumberWithPrefixToRequest(data.phone!),
      postCode: data.postCode!,
      state: data.state,
      streetLine1: data.streetLine1!,
      streetLine2: data.streetLine2,
      shippingMethod: data.shippingMethodId,
      shippingPoint: executeOrNull(data.shippingPoint, (point) => point.name),
    });

    return this.subscriptionApi.updateShippingAddress({ number, request }).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder
            .initSuccess('Adres dostawy dla subskrypcji został zaktualizowany.')
            .build(),
        );
      }),
    );
  }
}
